import { Injectable, signal, Signal } from '@angular/core';
import { Config } from '@environments/config';
import { Assistants, Chats, Experiences, Filters, Search, SessionInfo } from '@local/client-contracts';
import {
  getAssistantDescription,
  getAssistantTitle,
  getGeneralAssistantIcon,
  isEmbed,
  isExtension,
  isNativeWindow,
  isQuoteMessage,
  isTranslateMessage,
} from '@local/common-web';
import { EmbedOptions, EmbedService } from '@shared/embed.service';
import { AppService } from '@shared/services/app.service';
import { SessionService } from '@shared/services/session.service';
import { chain, isEmpty, isString, reduce, sortBy, upperFirst } from 'lodash';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AnswerNoResults, AnswerSearchItem, StaticSearchItem } from '../views';
import { StaticSearchItemType } from '../views/results/components/static-search-item/static-search-item.model';
import { AnswersSourceSettings, SearchRequest } from './search/client';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChatAssistantsService } from './chat-assistants.service';
import { ShowToasterService } from './show-toaster.service';
import { observable } from '@local/common';

export type ActiveReference = { searchId: string; index: number; position: number };

@Injectable({
  providedIn: 'root',
})
export class AnswerSearchService {
  private isNative = isNativeWindow();
  private isEmbed = isEmbed();
  private isExtension = isExtension();
  private cdnUrl: string;

  private readonly embedOption: Signal<EmbedOptions> = signal(null);
  readonly sessionInfo: Signal<SessionInfo> = signal(null);
  readonly assistantForSearch: Signal<Experiences.ExperienceItem[]> = signal(null);
  private _activeReference$ = new BehaviorSubject<ActiveReference>(null);

  isLauncher: boolean;

  @observable
  get activeReference$(): Observable<ActiveReference> {
    return this._activeReference$.asObservable();
  }

  constructor(
    private sessionService: SessionService,
    private embedService: EmbedService,
    private appService: AppService,
    private chatAssistantsService: ChatAssistantsService,
    private showToasterService: ShowToasterService
  ) {
    this.appService.windowStyle$.subscribe((b) => (this.isLauncher = b != 'standard'));

    if (this.embedService) {
      this.embedOption = toSignal(this.embedService.options$);
    }
    this.sessionInfo = toSignal(this.sessionService.current$);
    this.assistantForSearch = toSignal(this.chatAssistantsService.assistantForSearch$);

    this.cdnUrl = Config.blobs.cdnUrl;
  }

  private buildActionRequest(action: Chats.UserMessageAction): string {
    if (isQuoteMessage(action)) {
      let baseText: string;
      switch (action.type) {
        case 'improve':
          baseText = 'Please enhance the following text';
          break;
        case 'translate': {
          if (isTranslateMessage(action)) {
            baseText = `Please translate the following text into ${action.language} (give me only the following text)`;
          }
          break;
        }
        case 'summarize':
          baseText = 'Please summarize the following text';
          break;
      }
      return `${baseText}: "${action.quote}"`;
    }
  }

  async buildExternalRequest(
    request: SearchRequest<AnswersSourceSettings>,
    query: string
  ): Promise<Assistants.ExternalAnswersSearchRequest> {
    const req = await this.buildBaseRequest(request, query);
    if (!isEmpty(request.sourceSettings.action)) {
      query = this.buildActionRequest(request.sourceSettings.action);
    }
    return {
      ...req,
      experienceType: 'general',
      query: query || req.query,
      knowledgeType: Experiences.KnowledgeType.External,
      blobIds: request.sourceSettings.blobIds,
      action: request.sourceSettings.action,
    };
  }

  private async buildBaseRequest(
    request: SearchRequest<AnswersSourceSettings>,
    query: string
  ): Promise<Assistants.BaseAnswersSearchRequest> {
    const currentSession = await firstValueFrom(this.sessionService.current$);

    return {
      query,
      context: this.buildQuestionContext(currentSession, request.sourceSettings.source),
      sessionId: request.sessionId,
      experienceId: request.sourceSettings.assistantId,
      allowAllQuestionQueries: request.sourceSettings.allowAllQuestionQueries,
      chat: request.sourceSettings.chat,
      answerCacheTime: request.sourceSettings?.answerCacheTime,
    };
  }

  buildQuestionContext(currentSession: SessionInfo, location?: Assistants.AnswerLocationType): Assistants.QuestionContext {
    return {
      source: this.isNative ? 'Desktop' : 'Web',
      platform: this.getPlatformType(),
      location: this.getLocationType(location),
      simulation: currentSession.provider === 'unleash' ? 'true' : 'false',
    };
  }

  async buildInternalRequest(
    request: SearchRequest<AnswersSourceSettings>,
    query: string,
    mergeFilters: Filters.Values
  ): Promise<Assistants.InternalAnswersSearchRequest> {
    const baseRequest = await this.buildBaseRequest(request, query);

    return {
      ...baseRequest,
      experienceType: 'general',
      filters: mergeFilters,
      answersResultsTypes: ['KnowledgeBase', 'Cards', 'ResourceLookup', 'Conversations'] as Assistants.AnswersResultType[],
      knowledgeType: Experiences.KnowledgeType.Internal,
      onlyClassifyAi: request.sourceSettings.onlyClassifyAi,
      shouldClassifyAi: request.sourceSettings.shouldClassifyAi,
      disableSelfQuery: request.sourceSettings.disableSelfQuery,
      sort: request.sourceSettings.sorting,
    };
  }

  async buildAgenticRequest(request: SearchRequest<AnswersSourceSettings>, query: string): Promise<Assistants.ChatAnswersSearchRequest> {
    const req = await this.buildBaseRequest(request, query);
    if (!isEmpty(request.sourceSettings.action)) {
      query = this.buildActionRequest(request.sourceSettings.action);
    }
    return {
      ...req,
      experienceType: 'agentic',
      query: query || req.query,
      experienceId: request.sourceSettings.assistantId,
      blobIds: request.sourceSettings.blobIds,
      action: request.sourceSettings.action,
    };
  }

  private getLocationType(pageSource?: Assistants.AnswerLocationType): Assistants.AnswerLocationType {
    if (pageSource) {
      return pageSource;
    }
    if (this.isEmbed) {
      if (this.embedOption()?.slug) {
        return upperFirst(this.embedOption().slug.split(':')?.[0] || '') as Assistants.AnswerLocationType;
      }
      return 'Other';
    }
    return 'Search';
  }

  private getPlatformType() {
    if (this.isExtension) {
      return 'Extension';
    }
    if (this.isEmbed) {
      return 'Embed';
    }
    if (this.isLauncher) {
      return 'Launcher';
    }
    return 'App';
  }

  insertReferencesIntoText(
    federatedAnswer: Assistants.FederatedAnswer,
    resources: (Search.ExternalSourceItem | Search.ResultResourceItem | Chats.MessageResource)[]
  ): string {
    const text = federatedAnswer?.answer;
    if (!text) {
      return;
    }

    const references = federatedAnswer?.references;
    if (!references?.length || (!federatedAnswer.resourceIds?.length && !resources?.length)) {
      return text;
    }

    const sourceArray = resources?.length ? resources : federatedAnswer.resourceIds;
    const resourceIdToIndexMap = new Map(
      Object.entries(
        reduce(
          sourceArray,
          (acc, item, index) => {
            const id = isString(item) ? item : (item as Search.ResultItem)?.id || (item as Chats.MessageResource)?.resourceId || '';
            if (id) {
              acc[id] = index + 1;
            }
            return acc;
          },
          {}
        )
      )
    );

    let updatedText = text;

    sortBy(references, 'position')
      .reverse()
      .forEach((reference) => {
        const validIndices = chain(reference.referenceIds)
          .map((id) => resourceIdToIndexMap.get(id))
          .compact() // Filter out undefined/null values
          .uniq()
          .sortBy()
          .value();

        if (validIndices.length === 0) {
          return;
        }

        const referencesText = validIndices.map((index) => ` %%${index}%% `).join('');

        const position = reference.position;
        updatedText = `${updatedText.slice(0, position)}${referencesText}${updatedText.slice(position)}`;
      });

    return updatedText;
  }

  buildFollowUpItem(res: Assistants.AnswersSearchResponse): StaticSearchItem {
    return {
      type: 'static-search-item',
      icon: { type: 'font-icon', value: 'icon-bubble-dots' },
      title: AnswerNoResults.includes(res.status) ? 'Go to chat' : 'Ask a Follow-up',
      invokeType: 'follow-up-chat',
    };
  }

  buildAssistantAnswerStaticSearchItem(
    assistant: Experiences.ExperienceItem,
    invokeType: StaticSearchItemType = 'open-chat',
    searchId?: string
  ): StaticSearchItem {
    const searchAnswerItem: StaticSearchItem = {
      type: 'static-search-item',
      icon: getGeneralAssistantIcon(assistant),
      title: `Ask ${getAssistantTitle(assistant)}`,
      description: getAssistantDescription(assistant),
      invokeType,
      data: { assistant },
      searchId,
    };
    return searchAnswerItem;
  }

  buildLoadingItem(query: string, res: Assistants.AnswersSearchResponse, state?: Assistants.AnswerStatusType) {
    return {
      type: 'answer',
      query,
      state: state || res.status,
      searchId: res.searchId,
      debugInfo: res.debugInfo,
      intent: res.intent,
      progress: res.progress,
      reason: res.reason,
      searchParameters: res.searchParameters,
      agenticTools: res.agenticTools,
    } as AnswerSearchItem;
  }

  buildAnswerStaticSearchItem(
    currentSession: SessionInfo,
    searchId?: string,
    itemSettings?: Experiences.ExperienceGeneralSettings
  ): StaticSearchItem {
    const name = itemSettings.title || currentSession?.workspace?.name;
    const description = itemSettings?.description || 'Chat with Unleash AI for answers from your company’s knowledge';
    return {
      type: 'static-search-item',
      icon: { type: 'font-icon', value: 'icon-answer' },
      emoji: itemSettings?.emoji,
      title: `Ask ${name}`,
      description,
      invokeType: 'open-chat',
      searchId,
      isDefault: true,
    };
  }

  getFileDownloadUrl(fileId: string, chatId: string, assistantId: string, containerId: string | null): string {
    if (containerId) {
      return `${this.isEmbed ? this.cdnUrl + '/api' : ''}/assistant/files/${fileId}/content?id=${fileId}&containerId=${containerId}&chatId=${chatId}&experienceId=${assistantId}`;
    }
    return `${this.isEmbed ? this.cdnUrl + '/api' : ''}/assistant/files/${fileId}/content?id=${fileId}&chatId=${chatId}&experienceId=${assistantId}`;
  }

  openToasterError() {
    this.showToasterService.showToaster({
      title: 'Something went wrong. Please try again',
      id: 'error-answer-search',
    });
  }

  setReferenceActive(searchId?: string, index?: number, position?: number) {
    if (!searchId) {
      this._activeReference$.next(null);
      return;
    }
    this._activeReference$.next({ searchId, index, position });
  }
}
